import { inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES, VwApiRequestsService, VwApiResponse } from 'vwappservices';
import { LoaderService } from './loader.service';

export interface ApiResult<T = any> {
  ok: boolean;
  data: T;
  errorCode: string;
  message: string;
}

export interface ApiCallOptions {
  /** Show the global loader (default true). Pass a string to change its message. */
  loader?: boolean | string;
  /** Toast the error description on failure (default true). */
  errorToast?: boolean;
  /** Toast the backend's success message, when it sends one (default false). */
  successToast?: boolean;
}

/**
 * vwappservices resolves every non-2xx HTTP response as API_BREAK ('888888') with the
 * HttpErrorResponse in Data. vio_node_helpers sends application errors with a status code
 * (401/403/…), so unwrap the backend body to get the real errorCode/errorDescription.
 */
export const normalizeResponse = (res: VwApiResponse): VwApiResponse => {
  if (res?.errorCode === VW_ERROR_CODES.API_BREAK) {
    const body = res.Data?.error;
    if (body && typeof body === 'object' && typeof body.errorCode === 'string') {
      return { errorCode: body.errorCode, errorDescription: body.errorDescription ?? '', Data: body.Data };
    }
    const offline = res.Data?.status === 0;
    return {
      errorCode: VW_ERROR_CODES.API_BREAK,
      errorDescription: offline ? 'Unable to reach the server. Check your connection and try again.' : 'The request failed. Please try again.',
      Data: null,
    };
  }
  return res;
};

/**
 * App-level wrapper over VwApiRequestsService (which handles the token header, refresh-token
 * retries and the unauthorised handler). Adds the global loader, error toasts and a typed result.
 */
@Injectable({ providedIn: 'root' })
export class ApiRequestsService {
  private readonly vwApi = inject(VwApiRequestsService);
  private readonly loader = inject(LoaderService);
  private readonly toastr = inject(ToastrService);

  readonly NO_ERROR = VW_ERROR_CODES.SUCCESS;

  post<T = any>(url: string, criteria: Record<string, any> = {}, options: ApiCallOptions = {}): Promise<ApiResult<T>> {
    return this.run<T>(() => this.vwApi.postAPICall(url, criteria), options);
  }

  get<T = any>(url: string, criteria?: Record<string, any>, options: ApiCallOptions = {}): Promise<ApiResult<T>> {
    return this.run<T>(() => this.vwApi.getAPICall(url, criteria), options);
  }

  /** Multipart upload; the file goes in field "file" and criteria in "criteria". */
  upload<T = any>(url: string, file: File, criteria: Record<string, any> = {}, options: ApiCallOptions = {}): Promise<ApiResult<T>> {
    return this.run<T>(() => this.vwApi.uploadFile(url, file, criteria), { loader: 'Uploading...', ...options });
  }

  private async run<T>(call: () => Promise<VwApiResponse>, options: ApiCallOptions): Promise<ApiResult<T>> {
    const useLoader = options.loader !== false;
    if (useLoader) this.loader.show(typeof options.loader === 'string' ? options.loader : undefined);
    let res: VwApiResponse;
    try {
      res = normalizeResponse(await call());
    } catch (e) {
      console.error(e);
      res = { errorCode: VW_ERROR_CODES.API_BREAK, errorDescription: 'Unexpected error. Please try again.', Data: null };
    } finally {
      if (useLoader) this.loader.hide();
    }

    const ok = res.errorCode === VW_ERROR_CODES.SUCCESS;
    const message = res.errorDescription ?? '';
    if (!ok && options.errorToast !== false && res.errorCode !== VW_ERROR_CODES.UNAUTHORISED) {
      this.toastr.error(message || 'Something went wrong.');
    }
    if (ok && options.successToast && message) this.toastr.success(message);
    return { ok, data: res.Data as T, errorCode: res.errorCode, message };
  }
}
